import React, { useState, useEffect, useRef } from 'react';
import {
  Menu, X, ChevronDown, Globe, Cpu, Layers, Sparkles, Rocket,
  Zap, Terminal, ArrowRight, Home, Briefcase, MessageCircle,
  Shield, Cloud, Code, Database, TrendingUp, Users, Award,
  ChevronRight, Star, CircleDot, Compass, Hexagon, Boxes,
  LayoutGrid, Fingerprint, Microchip, Network
} from 'lucide-react';
import { Link } from 'react-router-dom';
import logo from '../assets/3D Logo (1).png';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const dropdownTimeoutRef = useRef(null);

  // Gradient colors extracted from logo (blue/cyan/teal palette)
  const logoGradient = {
    start: '#0B2B5E',  // Deep navy blue from logo
    mid: '#1A4A8B',    // Mid blue
    end: '#0EA5E9',    // Cyan/sky blue
    accent: '#06B6D4', // Teal/cyan accent
    glow: '#38BDF8'    // Bright cyan glow
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: 'Home', href: '/', icon: Compass },
    { name: 'About Us', href: '/about', icon: Users },
    { name: 'Services', href: '/services', icon: Hexagon, dropdown: true },
    { name: 'Blogs', href: '/blogs', icon: LayoutGrid },
    // { name: 'Contact', href: '/contact', icon: MessageCircle },
  ];

  const serviceCategories = [
    {
      title: 'Website Development',
      icon: Globe,
      items: [
        { name: 'Static Website Development', href: '/services/staticwebsites' },
        { name: 'Dynamic Website Development', href: '/services/websites' },
        { name: 'Ecommerce Website Development', href: '/services/ecommerce' },
      ]
    },
    {
      title: 'Digital Marketing',
      icon: TrendingUp,
      items: [
        { name: 'SEO SEM', href: '/services/marketing' },
        { name: 'Social Media Marketing', href: '/services/social-media-marketing' },
        { name: 'Graphics & Video Production', href: '/services/graphics-designing' },
        { name: 'Google My Business Profile Optimization', href: '/services/marketing' },
      ]
    },
      {
      title: 'Performance Marketing',
      icon: Zap,
      items: [
        { name: 'Meta Ads & Management', href: '/services/Metaads' },
        { name: 'Google Ads & Management', href: '/services/Googleads' },
        { name: 'Hotstar Ads & Management', href: '/services/Hotstarads' },
      ]
    },
    {
      title: 'Application Development',
      icon: LayoutGrid,
      href: '/services/apps'
    },
    {
      title: 'Custom Software Development',
      icon: Code,
      href: '/services/software'
    },
    
  
    {
      title: 'Whatsapp API Development',
      icon: MessageCircle,
      href: '/services/whatsapp-api'
    }
  ];

  const handleDropdownEnter = (name) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(name);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
      setHoveredIndex(null);
    }, 150);
  };

  return (
    <>
      <style jsx>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(5deg); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes borderPulse {
          0%, 100% { border-color: rgba(14, 165, 233, 0.3); }
          50% { border-color: rgba(14, 165, 233, 0.8); }
        }
        .animate-gradient-shift {
          background-size: 200% 200%;
          animation: gradientShift 8s ease infinite;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }
        .glow-text {
          text-shadow: 0 0 20px rgba(14, 165, 233, 0.5);
        }
      `}</style>

      {/* Animated gradient background that follows logo colors */}
      <div className="fixed inset-0 -z-20">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B2B5E] via-[#0A1A3A] to-[#061225]" />
        <div className="absolute top-0 left-0 w-full h-full opacity-30">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#0EA5E9] rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#06B6D4] rounded-full blur-[100px] animate-pulse delay-1000" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#1A4A8B] rounded-full blur-[150px] opacity-40" />
        </div>
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-50"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='60' height='60' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 60 0 L 0 0 0 60' fill='none' stroke='rgba(14,165,233,0.03)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")` }}
        />
      </div>

      <div className="fixed w-full z-50 top-0 transition-all duration-700" style={{ fontFamily: 'emoji' }}>
        <nav
          className={`w-full transition-all duration-700 ${scrolled
            ? 'bg-[#0A0F1F]/90 backdrop-blur-2xl border-b border-[#0EA5E9]/20 shadow-[0_8px_32px_rgba(14,165,233,0.15)]'
            : 'bg-gradient-to-b from-[#0A0F1F]/80 to-transparent backdrop-blur-md border-b border-[#0EA5E9]/10'
            }`}
          style={{
            boxShadow: scrolled ? `0 8px 32px rgba(14, 165, 233, 0.15), inset 0 1px 0 rgba(255,255,255,0.05)` : 'none'
          }}
        >
          <div className={`flex justify-between items-center max-w-[1920px] mx-auto transition-all duration-500 ${scrolled ? 'h-20 px-4 sm:px-8' : 'h-28 px-6 sm:px-12'}`}>

            {/* Logo Section */}
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center group cursor-pointer">
              <img
                src={logo}
                alt="VishwaDigitech Logo"
                className={`${scrolled ? 'h-16 lg:h-20' : 'h-24 lg:h-28'} w-auto object-contain transition-all duration-500 group-hover:scale-105`}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {menuItems.map((item, idx) => (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => item.dropdown && handleDropdownEnter(item.name)}
                  onMouseLeave={() => item.dropdown && handleDropdownLeave()}
                >
                  {item.dropdown ? (
                    <button
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${activeDropdown === item.name
                        ? 'text-[#0EA5E9] bg-white/5 shadow-lg'
                        : 'text-gray-300 hover:text-white hover:bg-white/5'
                        }`}
                      style={{
                        boxShadow: activeDropdown === item.name ? `0 0 15px rgba(14, 165, 233, 0.2)` : 'none'
                      }}
                    >
                      <item.icon className="h-4 w-4" />
                      {item.name}
                      <ChevronDown className={`h-3.5 w-3.5 transition-all duration-300 ${activeDropdown === item.name ? 'rotate-180 text-[#0EA5E9]' : ''}`} />
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-300 group relative overflow-hidden"
                    >
                      <item.icon className="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
                      <span className="relative z-10">{item.name}</span>
                      <span
                        className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300 rounded-full"
                        style={{ background: `linear-gradient(90deg, ${logoGradient.end}, ${logoGradient.mid})` }}
                      />
                    </Link>
                  )}

                  {/* Mega Dropdown with brand colors */}
                  {item.dropdown && activeDropdown === item.name && (
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[800px] rounded-2xl overflow-hidden animate-in fade-in slide-in-from-top-5 duration-300"
                      style={{
                        background: 'rgba(8, 12, 25, 0.98)',
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${logoGradient.end}33`,
                        boxShadow: `0 25px 50px -12px rgba(0,0,0,0.5), 0 0 0 1px ${logoGradient.end}20 inset`
                      }}
                      onMouseEnter={() => handleDropdownEnter(item.name)}
                      onMouseLeave={() => handleDropdownLeave()}
                    >
                      {/* Top accent bar */}
                      <div
                        className="absolute top-0 left-0 w-full h-1"
                        style={{ background: `linear-gradient(90deg, ${logoGradient.start}, ${logoGradient.end}, ${logoGradient.mid}, ${logoGradient.end}, ${logoGradient.start})` }}
                      />

                      <div className="relative p-5">
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
                          <div>
                            <h3
                              className="text-lg font-bold"
                              style={{ background: `linear-gradient(135deg, ${logoGradient.end}, ${logoGradient.mid})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}
                            >
                              Our Services
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">Comprehensive technology services tailored for your success</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex -space-x-2">
                              {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-[#0A0F1F] flex items-center justify-center">
                                  <Users className="h-3 w-3 text-gray-400" />
                                </div>
                              ))}
                            </div>
                            {/* <span className="text-xs text-gray-500">2,000+ clients</span> */}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-x-6 gap-y-8">
                          {serviceCategories.map((category, idx) => (
                            <div key={idx} className="flex flex-col gap-3">
                              {category.href ? (
                                <Link
                                  to={category.href}
                                  className="group/item flex items-center gap-3 p-2 -ml-2 rounded-xl hover:bg-white/5 transition-all duration-300"
                                  onClick={() => setActiveDropdown(null)}
                                >
                                  <div className="p-2 rounded-lg shadow-lg group-hover/item:scale-110 transition-all duration-300" style={{ background: `linear-gradient(135deg, ${logoGradient.mid}, ${logoGradient.end})` }}>
                                    <category.icon className="h-4 w-4 text-white" />
                                  </div>
                                  <h4 className="text-sm font-bold text-gray-100 group-hover/item:text-[#0EA5E9] transition-colors">{category.title}</h4>
                                </Link>
                              ) : (
                                <div className="flex items-center gap-3 p-2 -ml-2">
                                  <div className="p-2 rounded-lg shadow-lg" style={{ background: `linear-gradient(135deg, ${logoGradient.mid}, ${logoGradient.end})` }}>
                                    <category.icon className="h-4 w-4 text-white" />
                                  </div>
                                  <h4 className="text-sm font-bold text-gray-100">{category.title}</h4>
                                </div>
                              )}
                              
                              {category.items && category.items.length > 0 && (
                                <div className="flex flex-col gap-2.5 pl-11">
                                  {category.items.map((item, i) => (
                                    <Link
                                      key={i}
                                      to={item.href}
                                      className="text-xs text-gray-400 hover:text-[#0EA5E9] transition-colors flex items-center gap-1.5 group/link"
                                      onClick={() => setActiveDropdown(null)}
                                    >
                                      <div className="w-1 h-1 rounded-full bg-[#0EA5E9]/50 group-hover/link:bg-[#0EA5E9] group-hover/link:scale-150 transition-all duration-300" />
                                      {item.name}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        <div className="mt-4 pt-3 flex justify-between items-center border-t border-white/5">
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {[1, 2, 3].map((i) => (
                                <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#0EA5E9] mx-0.5 animate-pulse" />
                              ))}
                            </div>
                            {/* <span className="text-xs text-gray-500">Trusted by industry leaders worldwide</span> */}
                          </div>
                          <a
                            href="#services"
                            className="group/link flex items-center gap-1.5 text-xs font-medium transition-colors"
                            style={{ color: logoGradient.end }}
                          >
                            <span>Explore all services</span>
                            <ArrowRight className="h-3 w-3 transform group-hover/link:translate-x-1 transition-all duration-300" />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="hidden lg:flex items-center gap-2">
              <Link
                to="/contact"
                className="group relative overflow-hidden rounded-xl px-5 py-2 font-medium text-white transition-all duration-300 hover:scale-105 active:scale-95"
                style={{
                  background: `linear-gradient(135deg, ${logoGradient.mid}, ${logoGradient.end}, ${logoGradient.start})`,
                  backgroundSize: '200% 200%',
                  boxShadow: `0 0 20px ${logoGradient.end}60`
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative flex items-center gap-2 text-sm">
                  Contact Us
                  <Sparkles className="h-3.5 w-3.5 group-hover:rotate-12 transition-transform duration-300" />
                </span>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden relative w-10 h-10 rounded-xl flex items-center justify-center text-gray-300 hover:text-white transition-all duration-300"
              style={{
                background: isMobileMenuOpen ? `linear-gradient(135deg, ${logoGradient.mid}40, ${logoGradient.end}40)` : 'transparent'
              }}
            >
              {isMobileMenuOpen ? (
                <X className="relative h-5 w-5 animate-in spin-in-90 duration-200" />
              ) : (
                <Menu className="relative h-5 w-5" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden absolute top-full left-0 w-full px-3 pt-2 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] origin-top ${isMobileMenuOpen ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'
            }`}
        >
          <div
            className="rounded-2xl shadow-2xl overflow-hidden"
            style={{
              background: 'rgba(8, 12, 25, 0.98)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${logoGradient.end}30`
            }}
          >
            <div className="p-3 space-y-1 max-h-[75vh] overflow-y-auto">
              {menuItems.map((item, idx) => (
                <div key={item.name} className="animate-in slide-in-from-top-5 duration-300" style={{ animationDelay: `${idx * 50}ms` }}>
                  {item.dropdown ? (
                    <div>
                      <button
                        onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                        className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl text-gray-300 hover:bg-white/5 hover:text-[#0EA5E9] transition-all duration-300 group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-1.5 rounded-lg bg-white/5 group-hover:bg-[#0EA5E9]/20 transition-colors">
                            <item.icon className="h-4 w-4" />
                          </div>
                          <span className="font-medium">{item.name}</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 transition-all duration-300 ${activeDropdown === item.name ? 'rotate-180 text-[#0EA5E9]' : ''}`} />
                      </button>
                      <div className={`overflow-hidden transition-all duration-400 ${activeDropdown === item.name ? 'max-h-[600px] mt-1' : 'max-h-0'}`}>
                        <div className="p-3 bg-white/5 rounded-xl space-y-4 ml-2">
                          {serviceCategories.map((category, idx) => (
                            <div key={idx} className="flex flex-col gap-2">
                              {category.href ? (
                                <Link
                                  to={category.href}
                                  className="flex items-center gap-3 py-1 text-gray-200 hover:text-[#0EA5E9] transition-colors"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  <div className="p-1.5 rounded-lg" style={{ background: `linear-gradient(135deg, ${logoGradient.mid}, ${logoGradient.end})` }}>
                                    <category.icon className="h-3.5 w-3.5 text-white" />
                                  </div>
                                  <span className="text-sm font-semibold">{category.title}</span>
                                </Link>
                              ) : (
                                <div className="flex items-center gap-3 py-1">
                                  <div className="p-1.5 rounded-lg" style={{ background: `linear-gradient(135deg, ${logoGradient.mid}, ${logoGradient.end})` }}>
                                    <category.icon className="h-3.5 w-3.5 text-white" />
                                  </div>
                                  <span className="text-sm font-semibold text-gray-200">{category.title}</span>
                                </div>
                              )}
                              
                              {category.items && category.items.length > 0 && (
                                <div className="flex flex-col gap-2.5 pl-10 border-l border-white/10 ml-3 py-1">
                                  {category.items.map((item, i) => (
                                    <Link
                                      key={i}
                                      to={item.href}
                                      className="text-xs text-gray-400 hover:text-[#0EA5E9] transition-colors"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      {item.name}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className="flex items-center gap-3 px-4 py-3.5 rounded-xl font-medium text-gray-300 hover:bg-white/5 hover:text-[#0EA5E9] transition-all duration-300 group"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="p-1.5 rounded-lg bg-white/5 group-hover:bg-[#0EA5E9]/20 transition-colors">
                        <item.icon className="h-4 w-4" />
                      </div>
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}

              <div className="pt-4 pb-2 px-2 flex flex-col gap-2 border-t border-white/10 mt-2">
                <Link
                  to="/contact"
                  className="w-full py-3 rounded-xl font-medium text-white transition-all duration-300 flex items-center justify-center gap-2 group"
                  style={{
                    background: `linear-gradient(135deg, ${logoGradient.mid}, ${logoGradient.end})`,
                    boxShadow: `0 0 15px ${logoGradient.end}60`
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact Us
                  <Sparkles className="h-3.5 w-3.5 group-hover:rotate-12 transition-transform duration-300" />
                </Link>
              </div>

              <div className="pt-3 pb-2 px-3 flex justify-between text-[10px] text-gray-600">
                <span>© 2024 VishwaDigitech</span>
                <span className="flex items-center gap-1">
                  <Award className="h-3 w-3" style={{ color: logoGradient.end }} /> Enterprise Ready
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Missing Brain icon component
const Brain = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 4a4 4 0 0 1 3.5 6.1A4 4 0 0 1 16 14a4 4 0 0 1-4 4 4 4 0 0 1-4-4 4 4 0 0 1 .5-1.9A4 4 0 0 1 12 4z" />
    <path d="M12 8v8" />
    <path d="M8 12h8" />
    <circle cx="12" cy="4" r="1" />
    <circle cx="12" cy="20" r="1" />
    <circle cx="4" cy="12" r="1" />
    <circle cx="20" cy="12" r="1" />
  </svg>
);

export default Navbar;